import { FormatOptions } from 'components/common/FormatOptions/FormatOptions';
import { CodeNameModel } from 'components/common/types';
import { Store } from 'components/common/types/Store.types';
import { calculateSecondaryPanelPosition } from 'components/pages/Planner/PlannerLineActions/calculateSecondaryPanelPosition';
import PlannerSecondaryPanel from 'components/pages/Planner/PlannerSecondaryPanel';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import { canDisplayOptionResults } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hidePlannerSecondaryPanel } from 'store/dealManagement/reducer';
import { updateIsObjectiveSubsectionChange } from 'store/objective/reducer';
import { CircularLoadingSpinner } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import Search from 'lib/Search';
import { FormatsProps } from './Formats.types';

const Formats: React.FC<FormatsProps> = ({
  formatOptions,
  selectedFormats,
  onConfirm,
  cancelButtonLabel,
  confirmButtonLabel,
  isSubsection = false,
  hideExclude,
}) => {
  const dispatch = useDispatch();
  const isFetchingReferences = useSelector((state: Store) => state.dealManagement.isFetchingReferences);

  const {
    onCategoryDirectionSort,
    searchText,
    openCategories,
    onFilterTextChange,
    selectedOptions: selectedDealLineFormats,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    includedOptions,
    excludedOptions,
    isOptionSelected,
    isOptionIncluded,
    onOptionIncludeExcludeChange,
    onToggleCategory,
    setSelectedOptions,
    onCategoryIncludeAll,
    onCategoryExcludeAll,
    getCategoryIncludeState,
  } = useCategoryOptions({
    options: formatOptions as unknown as Record<string, CodeNameModel[]>,
    initialSelectedOptions: selectedFormats,
  });

  const isChange = useMemo(
    () => !isEqual(selectedFormats, selectedDealLineFormats),
    [selectedFormats, selectedDealLineFormats],
  );

  useEffect(() => {
    if (isSubsection) dispatch(updateIsObjectiveSubsectionChange(isChange));
  }, [isChange, isSubsection]);

  return (
    <PlannerSecondaryPanel
      title="Format"
      onSubmit={() => onConfirm(selectedDealLineFormats)}
      onCancel={() => dispatch(hidePlannerSecondaryPanel())}
      cancelButtonLabel={cancelButtonLabel}
      confirmButtonLabel={confirmButtonLabel}
      isDisabled={!isChange}
      isSubsection={isSubsection}
      showBackButton={isSubsection}
      isChange={isChange}
      customStyles={calculateSecondaryPanelPosition({ width: 400 })}
      pendoId="Format"
    >
      {isFetchingReferences ? (
        <CircularLoadingSpinner />
      ) : (
        <>
          <div className="mb-2">
            <Search value={searchText} placeholder="Search formats" onChange={onFilterTextChange} />
          </div>
          {canDisplayOptionResults(filteredOptions) ? (
            !!filteredOptions && (
              <FormatOptions
                filteredOptions={filteredOptions}
                selectedFormats={selectedDealLineFormats}
                onCategoryDirectionSort={onCategoryDirectionSort}
                sortDirection={sortDirection}
                onToggleCategory={onToggleCategory}
                setSelectedOptions={setSelectedOptions}
                getNumberOfFilteredOptions={getNumberOfFilteredOptions}
                isOptionIncluded={isOptionIncluded}
                isOptionSelected={isOptionSelected}
                onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
                openCategories={openCategories}
                includedOptions={includedOptions}
                excludedOptions={excludedOptions}
                hideExclude={hideExclude}
                onCategoryIncludeAll={onCategoryIncludeAll}
                onCategoryExcludeAll={onCategoryExcludeAll}
                getCategoryIncludeState={getCategoryIncludeState}
              />
            )
          ) : (
            <p>There are no results matching your search</p>
          )}
        </>
      )}
    </PlannerSecondaryPanel>
  );
};

export default Formats;
